import { FC, useEffect, useRef, useState } from "react";
import {
    select,
    scaleBand,
    scaleLinear,
    max,
    axisBottom,
    axisLeft,
    least,
} from "d3";
import { Box, Typography, TypographyProps } from "@mui/material";
import EmptyComponent from "../EmptyComponent";
import { getChartColors } from "../../helpers/getChartColors";
import CustomTooltip from "../CustomTooltip";

const colors = getChartColors();

export enum BAR_GRAPH_DIRECTION {
    HORIZONTAL,
    VERTICAL,
}

interface Series {
    key: string;
    label: string;
}

interface GroupedBarChartProps {
    data: Array<{ label: string;[key: string]: number | string }>;
    series: Series[];
    width?: string | number;
    height?: string | number;
    showXAxis?: boolean;
    showYAxis?: boolean;
    showLabels?: boolean;
    axisFontSize?: number;
    labelFontSize?: number;
    categoryFontSize?: number;
    title?: string;
    tooltipTitle?: string;
    titleProps?: TypographyProps;
    direction?: BAR_GRAPH_DIRECTION;
    categoryPosition?: "TOP" | "BOTTOM";
    showLabelInHeader?: boolean;
}

const DEFAULT_VERTICAL_MARGINS = { left: 40, right: 20, top: 20, bottom: 50 };
const DEFAULT_HORIZONTAL_MARGINS = { left: 60, right: 20, top: 20, bottom: 40 };
const numGridLines = 5;

const LegendRow = ({
    data,
    colors,
    fontSize = 14,
    minWidth = 50,
}: {
    data: Series[];
    colors: string[];
    fontSize?: number;
    minWidth?: number;
}) => (
    <Box
        sx={{
            display: "flex",
            flexWrap: "wrap",
            width: "auto",
            gap: 2,
            rowGap: 0.5,
            columnGap: 2,
            alignItems: "flex-start",
            mb: 2,
        }}
    >
        {data.map((s, index) => (
            <Box
                key={s.key}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    minWidth,
                    mb: 0.5,
                    mr: 2,
                }}
            >
                <Box
                    sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        mr: 1,
                        backgroundColor: colors[index],
                    }}
                />
                <Typography
                    fontSize={fontSize}
                    noWrap
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                >
                    {s.label}
                </Typography>
            </Box>
        ))}
    </Box>
);

const measureTextWidth = (text: string, font: string) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.font = font;
        return ctx.measureText(text).width;
    }
    return 0;
};

const adjustMargins = (
    data: any[],
    series: Series[],
    dimensions: { width: number; height: number },
    margins: typeof DEFAULT_VERTICAL_MARGINS,
    axisFontSize: number
) => {
    let localMargins = { ...margins };
    let innerWidth = dimensions.width - localMargins.left - localMargins.right;
    let innerHeight = dimensions.height - localMargins.top - localMargins.bottom;

    const categories = data.map((d) => d.label as string);
    const x0_initial = scaleBand().domain(categories).range([0, innerWidth]).padding(0.2);

    // Check rotation need
    let maxXLabelWidth = Math.max(...categories.map(lbl => measureTextWidth(lbl, `${axisFontSize}px sans-serif`)));
    const shouldRotate = maxXLabelWidth > x0_initial.bandwidth();

    if (shouldRotate) {
        const sin45 = Math.sin(Math.PI / 4);
        const extra = Math.ceil(maxXLabelWidth * sin45) + 12;
        localMargins.bottom = Math.max(localMargins.bottom, extra + 8);
        innerHeight = dimensions.height - localMargins.top - localMargins.bottom;
    }

    // Adjust Y axis margin
    const yMaxCandidate = max(
        data,
        (d) => max(series, (s) => (d[s.key] as number) ?? 0)
    ) ?? 0;
    const yTempScale = scaleLinear().domain([0, yMaxCandidate]).nice().range([innerHeight, 0]);
    const yTickVals = yTempScale.ticks(5);

    let maxYTextWidth = Math.max(...yTickVals.map(t => measureTextWidth(String(Math.round(t)), `${axisFontSize}px sans-serif`)));

    if (maxYTextWidth + 12 > localMargins.left) {
        localMargins.left = Math.ceil(maxYTextWidth + 12);
        innerWidth = dimensions.width - localMargins.left - localMargins.right;
        innerHeight = dimensions.height - localMargins.top - localMargins.bottom;
    }

    return { localMargins, innerWidth, innerHeight, shouldRotate, categories };
};

interface DrawChartOptions {
    data: any[];
    series: Series[];
    colors: string[];
    dimensions: { width: number; height: number };
    margins: typeof DEFAULT_VERTICAL_MARGINS;
    showXAxis: boolean;
    showYAxis: boolean;
    showLabels: boolean;
    axisFontSize: number;
    labelFontSize: number;
}

function showTooltip({
    event,
    d,
    chart,
    x0,
    data,
    series,
    colors
}: {
    event: any;
    d: any;
    chart: d3.Selection<SVGGElement, unknown, null, undefined>;
    x0: d3.ScaleBand<string>;
    data: any[];
    series: any[];
    colors: string[];
}) {
    const group = select((event.currentTarget as SVGRectElement).parentNode as SVGGElement);
    const bars = group.selectAll<SVGRectElement, any>("rect.bar");

    let minY = Infinity;
    let maxY = -Infinity;
    bars.each(function () {
        const bar = select(this);
        const y = parseFloat(bar.attr("y")) || 0;
        const h = parseFloat(bar.attr("height")) || 0;
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y + h);
    });

    group.select(".hover-bg")
        .attr("y", minY)
        .attr("height", Math.max(0, maxY - minY))
        .attr("fill", "#F2F1FF");

    const tallestBar = least(bars.nodes(), node => parseFloat(select(node).attr("y")));
    if (!tallestBar) return;
    const bar = select(tallestBar);

    const chartNode = chart.node() as SVGGElement;
    const svg = select(chartNode.ownerSVGElement ?? chartNode.parentNode) as d3.Selection<SVGSVGElement, unknown, null, undefined>;

    const transformAttr = chart.attr("transform") || chartNode.getAttribute("transform") || "";
    const m = /translate\(\s*([-\d.]+)[ ,]+([-\d.]+)\s*\)/.exec(transformAttr);
    const chartOffsetX = m ? +m[1] : 0;
    const chartOffsetY = m ? +m[2] : 0;

    const xPosLocal = (x0(d.parentLabel as string) ?? 0) + x0.bandwidth() / 2;
    const parentData = data.find(item => item.label === d.parentLabel)!;
    const lines = series.map((s, i) => {
        const value = parentData[s.key];
        let text = `${value} min`;
        if (i === 1) {
            const avg = parentData.avgPercentage ?? "";
            text += ` Avg ${avg}% of case`;
        }
        return { color: colors[i % colors.length], text: text.trim() };
    });

    const barYLocal = parseFloat(bar.attr("y")) - (lines.length * 20) - 5;

    let globalX = chartOffsetX + xPosLocal;
    let globalY = chartOffsetY + barYLocal;

    svg.selectAll(".bar-tooltip").remove();

    const tooltipGroup = svg.append("g")
        .attr("class", "bar-tooltip")
        .style("pointer-events", "none")
        .attr("transform", `translate(${globalX},${globalY})`);

    const textGroup = tooltipGroup.append("g");
    lines.forEach((line, i) => {
        const row = textGroup.append("g").attr("transform", `translate(-40,${i * 18})`);
        row.append("circle").attr("cx", 8).attr("cy", 8).attr("r", 5).attr("fill", line.color);
        row.append("text")
            .attr("x", 20)
            .attr("y", 12)
            .style("font-size", "12px")
            .style("fill", "#fff")
            .text(line.text);
    });

    const textBBox = (textGroup.node() as SVGGElement).getBBox();
    tooltipGroup.insert("rect", "g")
        .attr("x", textBBox.x - 10)
        .attr("y", textBBox.y - 8)
        .attr("width", textBBox.width + 20)
        .attr("height", textBBox.height + 16)
        .attr("rx", 10)
        .attr("ry", 10)
        .style("fill", "#1a1832")
        .style("opacity", 0.95);

    const tooltipBBox = (tooltipGroup.node() as SVGGElement).getBBox();

    const svgNode = svg.node() as SVGSVGElement;
    const svgWidth = parseFloat(svg.attr("width")) || svgNode.getBoundingClientRect().width;
    const svgHeight = parseFloat(svg.attr("height")) || svgNode.getBoundingClientRect().height;

    let newX = globalX;
    let newY = globalY;

    const left = newX + tooltipBBox.x;
    const right = left + tooltipBBox.width;
    const top = newY + tooltipBBox.y;
    const bottom = top + tooltipBBox.height;
    const pad = 8;

    if (left < 0) newX += -left + pad;
    if (right > svgWidth) newX -= (right - svgWidth) + pad;
    if (top < 0) newY += -top + pad;
    if (bottom > svgHeight) newY -= (bottom - svgHeight) + pad;

    tooltipGroup.attr("transform", `translate(${newX},${newY})`);
}

function hideTooltip(chart: d3.Selection<SVGGElement, unknown, null, undefined>) {
    const chartNode = chart.node() as SVGGElement;
    const svg = select(chartNode.ownerSVGElement ?? chartNode.parentNode);
    svg.selectAll(".bar-tooltip").remove();

    select(chartNode).selectAll(".hover-bg")
        .attr("fill", "transparent")
        .attr("height", 0);
}


const drawChart = (
    svgRef: React.RefObject<SVGSVGElement>,
    {
        data,
        series,
        colors,
        dimensions,
        margins,
        showXAxis,
        showYAxis,
        showLabels,
        axisFontSize,
        labelFontSize,
    }: DrawChartOptions
) => {
    const svg = select(svgRef.current);
    svg.selectAll("*").remove();

    const { localMargins, innerWidth, innerHeight, shouldRotate, categories } =
        adjustMargins(data, series, dimensions, margins, axisFontSize);

    const x0 = scaleBand().domain(categories).range([0, innerWidth]).padding(0.2);
    const x1 = scaleBand().domain(series.map(s => s.key)).range([0, x0.bandwidth()]).padding(0.05);
    const y = scaleLinear().domain([0, max(data, d => max(series, s => (d[s.key] as number) ?? 0)) ?? 0]).nice().range([innerHeight, 0]);

    const chart = svg.append("g").attr("transform", `translate(${localMargins.left},${localMargins.top})`);

    // Grid lines
    const yTickValues = Array.from({ length: numGridLines }, (_, i) => (i / (numGridLines - 1)) * (y.domain()[1] - y.domain()[0]));
    chart.append("g").attr("class", "grid").selectAll("line")
        .data(yTickValues)
        .enter().append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", d => y(d))
        .attr("y2", d => y(d))
        .attr("stroke", "#e0e0e0")
        .attr("stroke-width", 2);

    // Category groups
    const categoryGroups = chart.selectAll("g.category").data(data).enter().append("g")
        .attr("class", "category")
        .attr("transform", d => `translate(${x0(d.label as string)},0)`);

    categoryGroups.append("rect")
        .attr("class", "hover-bg")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", x0.bandwidth())
        .attr("height", innerHeight)
        .attr("rx", 10).attr("ry", 10)
        .attr("fill", "transparent");

    // Bars
    categoryGroups.selectAll("rect.bar")
        .data(d => series.map((s, i) => ({
            key: s.key,
            label: s.label,
            value: d[s.key] as number,
            color: colors[i % colors.length],
            parentLabel: d.label
        })))
        .enter().append("rect")
        .attr("class", "bar")
        .attr("x", d => x1(d.key)!)
        .attr("y", d => y(d.value))
        .attr("width", x1.bandwidth())
        .attr("height", d => innerHeight - y(d.value))
        .attr("fill", d => d.color)
        .on("mouseover", function (event, d) {
            showTooltip({ event, d, chart, x0, data, series, colors });
        })
        .on("mouseout", function () {
             hideTooltip(chart);
        });

    if (showLabels) {
        categoryGroups.selectAll("text.bar-label")
            .data(d => series.map((s, i) => ({
                key: s.key,
                label: s.label,
                value: d[s.key] as number,
                color: colors[i % colors.length]
            })))
            .enter().append("text")
            .attr("class", "bar-label")
            .attr("x", d => (x1(d.key) ?? 0) + x1.bandwidth() / 2)
            .attr("y", d => y(d.value) - 5)
            .attr("text-anchor", "middle")
            .attr("fill", "black")
            .style("font-size", labelFontSize)
            .text(d => d.value);
    }

    if (showYAxis) {
        const yAxisG = chart.append("g").call(axisLeft(y));
        yAxisG.call(g => g.select(".domain").remove());
        yAxisG.call(g => g.selectAll("line").remove());
        yAxisG.selectAll("text").attr("fill", "rgba(178, 178, 178, 1)").style("font-size", axisFontSize + "px");
    }

    if (showXAxis) {
        const xAxisG = chart.append("g").attr("transform", `translate(0,${innerHeight})`).call(axisBottom(x0));
        xAxisG.call(g => g.select(".domain").remove());
        xAxisG.call(g => g.selectAll("line").remove());
        const labels = xAxisG.selectAll("text").attr("fill", "rgba(178, 178, 178, 1)").style("font-size", axisFontSize + "px");

        if (shouldRotate) {
            labels.attr("text-anchor", "end").attr("transform", "rotate(-45)").attr("dx", "-0.8em").attr("dy", "0.15em");
        } else {
            labels.attr("text-anchor", "middle").attr("transform", "rotate(0)").attr("dx", "0").attr("dy", "0.71em");
        }
    }
};

const GroupBarChart: FC<GroupedBarChartProps> = ({
    data,
    series,
    width = "100%",
    height = "100%",
    showXAxis = true,
    showYAxis = true,
    showLabels = false,
    axisFontSize = 14,
    labelFontSize = 12,
    categoryFontSize = 14,
    title,
    tooltipTitle,
    titleProps,
    direction = BAR_GRAPH_DIRECTION.VERTICAL,
    categoryPosition,
    showLabelInHeader = true,
}) => {
    const isHorizontal = direction === BAR_GRAPH_DIRECTION.HORIZONTAL;
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [margins] = useState(isHorizontal ? DEFAULT_HORIZONTAL_MARGINS : DEFAULT_VERTICAL_MARGINS);

    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const { width, height } = entries[0].contentRect;
                setDimensions({ width, height });
            }
        });
        if (containerRef.current) resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [data?.length]);

    useEffect(() => {
        if (!data?.length || !dimensions.width || !dimensions.height) return;
        drawChart(svgRef, {
            data,
            series,
            colors,
            dimensions,
            margins,
            showXAxis,
            showYAxis,
            showLabels,
            axisFontSize,
            labelFontSize,
        });
    }, [data, series, dimensions, margins, showXAxis, showYAxis, showLabels, axisFontSize, labelFontSize]);

    return (
        <Box flex={1} display="flex" flexDirection="column" sx={{ height: "100%" }}>
            {title && (
                <Box display="flex" flexDirection="row" sx={{ width: '100%', justifyContent: 'space-between' }}>
                    <CustomTooltip title={tooltipTitle}>
                        <Typography
                            variant="h6"
                            color="custom.foreground.secondary"
                            mb={3}
                            sx={titleProps}
                        >
                            {title}
                        </Typography>
                    </CustomTooltip>
                    {showLabelInHeader && (
                        <LegendRow data={series} colors={colors} fontSize={categoryFontSize} />
                    )}
                </Box>
            )}

            {data?.length > 0 ? (
                <Box flex={1} display="flex" flexDirection={categoryPosition === "BOTTOM" ? "column-reverse" : "column"} gap={3}>
                    <Box ref={containerRef} flex={1} sx={{ width, height }}>
                        <svg ref={svgRef} style={{ width: "100%", height: "100%" }}></svg>
                    </Box>
                </Box>
            ) : (
                <EmptyComponent />
            )}
        </Box>
    );
};

export default GroupBarChart;